import { APP_INITIALIZER, EnvironmentProviders, inject, LOCALE_ID, Provider } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { getPersistedSaiLang } from 'app/core/transloco/language-preference.util';
import { TranslocoHttpLoader } from 'app/core/transloco/transloco.http-loader';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

/**
 * Bootstrap pieces every SAI Angular application (the dashboard and the standalone messenger) must share:
 * scheme/theme persistence, language, locale and Fuse screen breakpoints. One copy, so the two apps cannot
 * drift apart in how they read the user's saved language and colour scheme.
 */

/** Matches classy.component toggleTheme() - read on bootstrap so refresh keeps dark/light. */
export function getPersistedSchemeAndTheme(): {
    scheme: 'light' | 'dark';
    theme: string;
} {
    try {
        // Dark mode follows the environment's own switch (`showDarkMode`), not the build mode: production builds
        // that enable the toggle must also restore the saved choice on reload, otherwise a user who picked dark
        // is sent back to light on every refresh.
        if (!environment.showDarkMode) {
            return { scheme: 'light', theme: 'theme-default' };
        }
        if (typeof localStorage === 'undefined') {
            return { scheme: 'light', theme: 'theme-default' };
        }
        const stored = localStorage.getItem('theme');
        if (stored === 'dark') {
            return { scheme: 'dark', theme: 'theme-default-dark' };
        }
        return { scheme: 'light', theme: 'theme-default' };
    } catch {
        return { scheme: 'light', theme: 'theme-default' };
    }
}

/**
 * Standalone routes (e.g. `/reports/report/:id`) skip LayoutComponent, which normally
 * applies body.light/dark. Without this, Fuse CSS vars like --fuse-bg-card are undefined
 * and MatDialog fallbacks render as dark on an otherwise light page.
 */
export function applyPersistedBodySchemeAndTheme(): void {
    const { scheme, theme } = getPersistedSchemeAndTheme();
    applySchemeAndTheme(scheme, theme);
}

/**
 * Put `scheme` (light/dark) and `theme` classes on the body, and the theme class on the CDK overlay container
 * when given, so dialogs and menus (which render outside the body's own tree) follow. This is what the dashboard
 * layout does for its own routes; apps without that layout (the standalone messenger) call it directly.
 */
export function applySchemeAndTheme(scheme: 'light' | 'dark', theme: string, overlayContainer?: HTMLElement): void {
    if (typeof document === 'undefined') {
        return;
    }

    const body = document.body;

    body.classList.remove('light', 'dark');
    body.classList.add(scheme);

    body.classList.forEach((className: string) => {
        if (className.startsWith('theme-')) {
            body.classList.remove(className);
        }
    });
    body.classList.add(theme);

    if (overlayContainer) {
        overlayContainer.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                overlayContainer.classList.remove(className);
            }
        });
        overlayContainer.classList.add(theme);
    }

    // The pre-boot splash reads this class (index.html), so the next load starts in the same scheme.
    document.documentElement.classList.toggle('splash-theme-dark', scheme === 'dark');
}

/** Luxon date formats shared by the Material date adapter in both apps. */
export const CUSTOM_DATE_FORMATS = {
    parse: {
        dateInput: 'dd/MM/yyyy', // Luxon expects lowercase for 'dd', 'MM', and 'yyyy'.
    },
    display: {
        dateInput: 'dd/MM/yyyy', // Correct the case here as well.
        monthYearLabel: 'MMMM yyyy', // Correct format for month-year labels.
        dateA11yLabel: 'dd/MM/yyyy', // Accessible labels also follow the same format.
        monthYearA11yLabel: 'MMMM yyyy',
    },
};

export const SAI_FUSE_SCREENS = {
    xs: '420px',
    sm: '600px',
    md: '960px',
    lg: '1280px',
    xl: '1440px',
};

export const SAI_FUSE_THEMES = [
    { id: 'theme-default', name: 'Default' },
    { id: 'theme-default-dark', name: 'Default Dark' },

    { id: 'theme-brand', name: 'Brand' },
    { id: 'theme-brand-dark', name: 'Brand Dark' },

    { id: 'theme-teal', name: 'Teal' },
    { id: 'theme-teal-dark', name: 'Teal Dark' },

    { id: 'theme-rose', name: 'Rose' },
    { id: 'theme-rose-dark', name: 'Rose Dark' },

    { id: 'theme-purple', name: 'Purple' },
    { id: 'theme-purple-dark', name: 'Purple Dark' },

    { id: 'theme-amber', name: 'Amber' },
    { id: 'theme-amber-dark', name: 'Amber Dark' },
];

/** Locale, Transloco (en/ar) and the language initializer. Arabic locale data is registered in each `main.ts`. */
export const provideSaiI18n = (): Array<Provider | EnvironmentProviders> => [
    // Locale - follows the persisted UI language so dates/numbers format in Arabic
    // when the app is in Arabic.
    { provide: LOCALE_ID, useFactory: (): string => (getPersistedSaiLang() === 'ar' ? 'ar' : 'en-US') },
    { provide: MAT_DATE_LOCALE, useFactory: (): string => (getPersistedSaiLang() === 'ar' ? 'ar' : 'en-GB') },

    // Transloco Config
    provideTransloco({
        config: {
            availableLangs: [
                {
                    id: 'en',
                    label: 'English',
                },
                {
                    id: 'ar',
                    label: 'Arabic',
                },
            ],
            defaultLang: 'en',
            fallbackLang: 'en',
            reRenderOnLangChange: true,
            prodMode: true,
        },
        loader: TranslocoHttpLoader,
    }),
    {
        provide: APP_INITIALIZER,
        useFactory: () => {
            const translocoService = inject(TranslocoService);
            // Arabic is disabled when showArabicLanguage is false (production).
            if (!environment.showArabicLanguage) {
                try {
                    localStorage.setItem('language', 'en');
                    localStorage.setItem('prefLang', 'en');
                } catch {
                    /* ignore */
                }
                document.documentElement.lang = 'en';
                document.documentElement.dir = 'ltr';
            }
            const defaultLang = environment.showArabicLanguage ? translocoService.getDefaultLang() : 'en';
            translocoService.setActiveLang(defaultLang);
            // Honour the persisted UI language at bootstrap so the WHOLE app - including
            // public pages (marketing, sign-in, terms) that never load the authenticated
            // layout's language switcher - renders in the saved language and direction.
            const lang = getPersistedSaiLang();
            translocoService.setActiveLang(lang);
            if (typeof document !== 'undefined') {
                document.documentElement.lang = lang;
                document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
            }

            return () => firstValueFrom(translocoService.load(lang));
        },
        multi: true,
    },
];

/** Remember the user's light/dark choice (same key the dashboard toggle writes, so both apps agree on one origin). */
export function persistSchemeChoice(scheme: 'light' | 'dark'): void {
    try {
        localStorage.setItem('theme', scheme);
    } catch {
        /* private mode: the choice then lasts for this page only */
    }
}
